/**
 * Product codes are `beforeFirstDot.afterFirstDot.afterSecondDot`
 * (e.g. 101.01.51). Sort by those three pieces, in that order.
 * A numeric piece is compared as a number, so 51 comes before 102 and
 * 2 comes before 10. Anything after the second dot stays one piece.
 *
 * @param {string} code
 * @returns {[string, string, string]}
 */
export function splitProductCodeSegments(code) {
    const text = String(code ?? '');
    const firstDot = text.indexOf('.');
    if (firstDot === -1) {
        return [text, '', ''];
    }
    const secondDot = text.indexOf('.', firstDot + 1);
    if (secondDot === -1) {
        return [text.slice(0, firstDot), text.slice(firstDot + 1), ''];
    }
    return [
        text.slice(0, firstDot),
        text.slice(firstDot + 1, secondDot),
        text.slice(secondDot + 1),
    ];
}

/**
 * @param {string} left
 * @param {string} right
 * @returns {number}
 */
function compareProductCodeSegment(left, right) {
    const a = String(left ?? '');
    const b = String(right ?? '');
    const aNumeric = a === '' || /^\d+$/.test(a);
    const bNumeric = b === '' || /^\d+$/.test(b);
    if (aNumeric && bNumeric) {
        return (Number(a) || 0) - (Number(b) || 0);
    }
    if (aNumeric !== bNumeric) {
        return aNumeric ? -1 : 1;
    }
    return a.localeCompare(b, 'ro', { numeric: true, sensitivity: 'base' });
}

/**
 * @param {unknown} leftCode
 * @param {unknown} rightCode
 * @returns {number}
 */
export function compareProductCodeStrings(leftCode, rightCode) {
    const left = String(leftCode ?? '');
    const right = String(rightCode ?? '');
    const leftSegmented = left.includes('.');
    const rightSegmented = right.includes('.');
    if (!leftSegmented && !rightSegmented) {
        return left.localeCompare(right);
    }
    if (leftSegmented !== rightSegmented) {
        return leftSegmented ? -1 : 1;
    }
    const leftParts = splitProductCodeSegments(left);
    const rightParts = splitProductCodeSegments(right);
    for (let index = 0; index < 3; index += 1) {
        const cmp = compareProductCodeSegment(leftParts[index], rightParts[index]);
        if (cmp !== 0) {
            return cmp;
        }
    }
    return 0;
}

/**
 * Sorts products by product code: the part before the first dot, then the
 * part between the dots, then the part after the second dot.
 *
 * Codes without a dot stay in string order and sort after dotted codes.
 *
 * @param {{ product_code?: string }} a
 * @param {{ product_code?: string }} b
 * @param {number} [direction=1] Positive for ascending, negative for descending.
 * @returns {number}
 */
export function sortByProductCode(a, b, direction = 1) {
    const dir = Number(direction) < 0 ? -1 : 1;
    const left = a?.product_code;
    const right = b?.product_code;
    if (left == null && right == null) {
        return 0;
    }
    if (left == null) {
        return dir;
    }
    if (right == null) {
        return -dir;
    }
    return dir * compareProductCodeStrings(left, right);
}
